import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Pagination } from "@/components/ui/Pagination";
import { Badge } from "@/components/ui/Badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Modal } from "@/components/ui/Modal";

type SnapshotSummary = {
  id: number;
  shop_name: string;
  source: "extension" | "server";
  url: string;
  crawled_at: string | null;
  processed_at: string | null;
  parse_status: "pending" | "processed" | "failed";
  stop_reason: string | null;
  next_url: string | null;
  extracted_count: number | null;
  matched_count: number | null;
  skipped_no_game_count: number | null;
  errors_count: number | null;
  processing_duration_ms: number | null;
  html_size_bytes: number;
  created_at: string;
};

type SnapshotDetail = SnapshotSummary & {
  parse_error: string | null;
  form_params: unknown;
  html_preview: string;
  html_truncated: boolean;
  linked_listings_count: number;
};

type SnapshotsResponse = {
  snapshots: SnapshotSummary[];
  stats: {
    total: number;
    last_24h: number;
    processed: number;
    failed: number;
    matched_24h: number;
    extracted_24h: number;
    avg_duration_ms: number | null;
  };
  shop_names: string[];
  sources: string[];
  pagination: {
    total_count: number;
    total_pages: number;
    current_page: number;
    per_page: number;
  };
};

function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

function formatDate(dateStr: string | null) {
  return dateStr ? new Date(dateStr).toLocaleString("lt-LT") : "—";
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function statusBadge(status: SnapshotSummary["parse_status"]) {
  const variant = status === "processed" ? "success" : status === "failed" ? "danger" : "default";
  return <Badge variant={variant}>{status}</Badge>;
}

export function AdminPageSnapshotsPage() {
  const [data, setData] = useState<SnapshotsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [shop, setShop] = useState("");
  const [parseStatus, setParseStatus] = useState("");
  const [source, setSource] = useState("");
  const [detail, setDetail] = useState<SnapshotDetail | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), per_page: "25" });
    if (shop) params.set("shop_name", shop);
    if (parseStatus) params.set("parse_status", parseStatus);
    if (source) params.set("source", source);

    try {
      const res = await fetchWithAuth(`/admin/page_snapshots?${params}`);
      const json = await res.json();
      setData(json);
    } finally {
      setLoading(false);
    }
  }, [page, shop, parseStatus, source]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadDetail = async (id: number) => {
    const res = await fetchWithAuth(`/admin/page_snapshots/${id}`);
    setDetail(await res.json());
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        ← Admin valdymo skydas
      </Link>
      <h1 className="text-2xl font-bold text-gray-900">Crawl snapshots</h1>
      <p className="mt-1 text-sm text-gray-500">List-page crawl snapshots — Chrome extension pushes (source: extension) and server-side scrapers (source: server). Retained for 1 week.</p>

      {data && (
        <div className="mt-4 grid gap-4 md:grid-cols-4">
          <StatBox label="Total stored" value={data.stats.total} />
          <StatBox label="Last 24h" value={data.stats.last_24h} sub={`${data.stats.extracted_24h} extracted, ${data.stats.matched_24h} matched`} />
          <StatBox label="Failed" value={data.stats.failed} tone={data.stats.failed > 0 ? "warn" : "default"} />
          <StatBox label="Avg duration" value={data.stats.avg_duration_ms != null ? `${data.stats.avg_duration_ms} ms` : "—"} sub="last 24h" />
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <select
          value={shop}
          onChange={(e) => { setShop(e.target.value); setPage(1); }}
          className="input w-48"
        >
          <option value="">All shops</option>
          {data?.shop_names.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={parseStatus}
          onChange={(e) => { setParseStatus(e.target.value); setPage(1); }}
          className="input w-48"
        >
          <option value="">Any status</option>
          <option value="processed">processed</option>
          <option value="failed">failed</option>
          <option value="pending">pending</option>
        </select>
        <select
          value={source}
          onChange={(e) => { setSource(e.target.value); setPage(1); }}
          className="input w-48"
        >
          <option value="">Any source</option>
          {data?.sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : data && data.snapshots.length > 0 ? (
        <>
          <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Shop</Th>
                  <Th>Source</Th>
                  <Th>Status</Th>
                  <Th>Crawled</Th>
                  <Th>Extracted</Th>
                  <Th>Matched</Th>
                  <Th>Skipped</Th>
                  <Th>Stop reason</Th>
                  <Th>Duration</Th>
                  <Th>Body size</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.snapshots.map((s) => (
                  <tr key={s.id} className="cursor-pointer hover:bg-gray-50" onClick={() => loadDetail(s.id)}>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-primary-800 hover:underline">{s.shop_name}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{s.source}</td>
                    <td className="px-4 py-3">{statusBadge(s.parse_status)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{formatDate(s.crawled_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{s.extracted_count ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{s.matched_count ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{s.skipped_no_game_count ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{s.stop_reason ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{s.processing_duration_ms != null ? `${s.processing_duration_ms} ms` : "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatBytes(s.html_size_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.pagination && (
            <div className="mt-6">
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      ) : (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white py-12 text-center">
          <p className="text-gray-500">No snapshots in the retention window.</p>
        </div>
      )}

      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail ? `Snapshot #${detail.id} — ${detail.shop_name}` : ""}
        size="4xl"
      >
        {detail && <SnapshotDetailView snapshot={detail} />}
      </Modal>
    </div>
  );
}

function StatBox({ label, value, sub, tone = "default" }: { label: string; value: number | string; sub?: string; tone?: "default" | "warn" }) {
  const border = tone === "warn" ? "border-red-200 bg-red-50" : "border-gray-200 bg-white";
  return (
    <div className={`rounded-xl border ${border} p-4`}>
      <p className="text-sm text-gray-600">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{children}</th>;
}

function SnapshotDetailView({ snapshot }: { snapshot: SnapshotDetail }) {
  return (
    <div className="space-y-4 text-sm">
      <Row label="URL">
        <a className="break-all text-primary-700 hover:underline" href={snapshot.url} target="_blank" rel="noreferrer">{snapshot.url}</a>
      </Row>
      <Row label="Status">{statusBadge(snapshot.parse_status)} <span className="ml-2 text-xs text-gray-500">source: {snapshot.source}</span> {snapshot.stop_reason && <span className="ml-2 text-gray-500">({snapshot.stop_reason})</span>}</Row>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Row label="Crawled at">{formatDate(snapshot.crawled_at)}</Row>
        <Row label="Processed at">{formatDate(snapshot.processed_at)}</Row>
        <Row label="Duration">{snapshot.processing_duration_ms != null ? `${snapshot.processing_duration_ms} ms` : "—"}</Row>
        <Row label="Extracted">{snapshot.extracted_count ?? "—"}</Row>
        <Row label="Matched">{snapshot.matched_count ?? "—"}</Row>
        <Row label="Skipped (no game)">{snapshot.skipped_no_game_count ?? "—"}</Row>
        <Row label="Errors">{snapshot.errors_count ?? "—"}</Row>
        <Row label="HTML size">{formatBytes(snapshot.html_size_bytes)}</Row>
        <Row label="Linked listings">{snapshot.linked_listings_count}</Row>
      </div>
      {snapshot.next_url && (
        <Row label="Next URL"><span className="break-all text-gray-700">{snapshot.next_url}</span></Row>
      )}
      {snapshot.parse_error && (
        <Row label="Parse error">
          <pre className="overflow-x-auto rounded bg-red-50 p-3 text-xs text-red-800">{snapshot.parse_error}</pre>
        </Row>
      )}
      <Row label={`HTML preview${snapshot.html_truncated ? " (truncated)" : ""}`}>
        <pre className="max-h-72 overflow-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800">{snapshot.html_preview}</pre>
      </Row>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}
